/**
 * Service for encrypting/decrypting chat history for public/shared computers
 * Uses Web Crypto API with AES-256-GCM encryption
 */

export class ChatEncryptionService {
  /**
   * Derive encryption key from user credentials
   */
  async deriveKey(userId: string, userSecret: string): Promise<CryptoKey> {
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(userId + userSecret),
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    const salt = new TextEncoder().encode('neko-assistant-chat-salt');

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Encrypt data
   */
  async encrypt(
    data: string,
    userId: string,
    userSecret: string
  ): Promise<string> {
    const key = await this.deriveKey(userId, userSecret);
    const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for GCM
    const encodedData = new TextEncoder().encode(data);

    const encrypted = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      key,
      encodedData
    );

    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    // Convert to base64 for storage
    return btoa(String.fromCharCode(...Array.from(combined)));
  }

  /**
   * Decrypt data
   */
  async decrypt(
    encryptedData: string,
    userId: string,
    userSecret: string
  ): Promise<string> {
    const key = await this.deriveKey(userId, userSecret);

    // Decode from base64
    const combined = Uint8Array.from(atob(encryptedData), (c) =>
      c.charCodeAt(0)
    );

    // Extract IV and encrypted data
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);

    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      key,
      encrypted
    );

    return new TextDecoder().decode(decrypted);
  }

  /**
   * Check if Web Crypto API is available
   */
  isAvailable(): boolean {
    return 'crypto' in window && 'subtle' in window.crypto;
  }
}

export const chatEncryptionService = new ChatEncryptionService();
